import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Box, 
  CssBaseline, 
  useMediaQuery, 
  useTheme,
  styled,
  Container,
  Paper,
  Fab,
  Tooltip,
  Toolbar,
} from '@mui/material';
import { 
  Keyboard as KeyboardIcon 
} from '@mui/icons-material';
import { RootState } from '../../store';
import { setIsMobileView, openUploadDialog, closeUploadDialog } from '../../store/slices/uiSlice';
import Sidebar from '../navigation/Sidebar';
import TopBar from '../navigation/TopBar';
import ConfirmDialog from '../ui/ConfirmDialog';
import KeyboardShortcutsHelp from '../dialogs/KeyboardShortcutsHelp';
import BackgroundUploadNotification from '../ui/BackgroundUploadNotification';
import SimpleFolderUploadDialog from '../dialogs/SimpleFolderUploadDialog';
import folderService from '../../services/folderService';
import {
  resolveUploadFolderId,
  parseFilesViewFromSearch,
  parseRouteFolderId,
  isUploadDisabledForView,
  ROOT_UPLOAD_ERROR
} from '../../utils/uploadContext';
import { useSnackbar } from 'notistack';

// Constants
const TOPBAR_HEIGHT = 64;

// Styled components for better control
const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}));

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  paddingTop: theme.spacing(3),
  height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
  marginTop: `${TOPBAR_HEIGHT}px`,
  overflow: 'auto',
  position: 'relative',
  // Keep below fixed TopBar/Drawer so content cannot paint over the sidebar
  zIndex: 0,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const TopBarContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.drawer + 10, // Ensure TopBar is always on top
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  padding: 0,
  maxWidth: '100%',
  [theme.breakpoints.up('xl')]: {
    maxWidth: '1800px',
  },
}));

const KeyboardHelpFab = styled(Fab, {
  shouldForwardProp: (prop) => prop !== '$raised'
})<{ $raised?: boolean }>(({ theme, $raised }) => ({
  position: 'fixed',
  // Sit left of the FileBrowser upload FAB (right: 20, ~56px wide) with a clear gap
  bottom: $raised ? theme.spacing(11) : theme.spacing(2.5),
  right: theme.spacing(11),
  zIndex: theme.zIndex.drawer - 1,
  transition: theme.transitions.create('bottom', { duration: 200 }),
}));

const SkipLink = styled('a')(({ theme }) => ({
  position: 'absolute',
  top: '-40px',
  left: 0,
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(1, 2),
  zIndex: theme.zIndex.tooltip,
  transition: 'top 0.2s ease-in-out',
  textDecoration: 'none',
  borderRadius: '0 0 4px 0',
  '&:focus': {
    top: 0,
  },
}));

const MainLayout: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { isSelectionMode, uploadDialogOpen, uploadDialogFolderId } = useSelector(
    (state: RootState) => state.ui
  );
  const { currentFolder, lastBrowsedFolderId } = useSelector((state: RootState) => state.folders);
  const navigate = useNavigate();
  
  // State for keyboard shortcuts dialog
  const [isKeyboardShortcutsOpen, setIsKeyboardShortcutsOpen] = useState(false);
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);

  useEffect(() => {
    folderService.getFolder('root').then((f) => setRootFolderId(f.id)).catch(() => {});
  }, []);

  const filesViewFromRoute = parseFilesViewFromSearch(location.search);
  const routeFolderId = parseRouteFolderId(location.pathname);

  const layoutUploadFolderId = useMemo(
    () =>
      uploadDialogFolderId ||
      resolveUploadFolderId({
        routeFolderId,
        currentFolderId: currentFolder?.id,
        filesView: filesViewFromRoute,
        rootFolderId,
        lastBrowsedFolderId
      }),
    [
      uploadDialogFolderId,
      routeFolderId,
      currentFolder?.id,
      filesViewFromRoute,
      rootFolderId,
      lastBrowsedFolderId
    ]
  );

  const { enqueueSnackbar } = useSnackbar();

  const requestLayoutUpload = () => {
    if (layoutUploadFolderId && !isUploadDisabledForView(filesViewFromRoute)) {
      dispatch(openUploadDialog({ folderId: layoutUploadFolderId }));
      return;
    }
    enqueueSnackbar(ROOT_UPLOAD_ERROR, { variant: 'error' });
    navigate('/files?upload=blocked');
  };

  // Handle keyboard shortcut F1 to open keyboard shortcuts help
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent F1 from showing browser help
      if (e.key === 'F1') {
        e.preventDefault();
        setIsKeyboardShortcutsOpen(true);
      }
      
      // Add common accessibility shortcuts
      if (e.ctrlKey && e.altKey) {
        switch (e.key) {
          case 'h': // Go home
            e.preventDefault();
            navigate('/');
            break;
          case 's': // Focus search
            e.preventDefault();
            const searchEl = document.querySelector('[role="search"] input') as HTMLInputElement;
            if (searchEl) searchEl.focus();
            break;
          case 'n': // Navigation menu
            e.preventDefault();
            const navEl = document.querySelector('nav a') as HTMLAnchorElement;
            if (navEl) navEl.focus();
            break;
          case 'k': // Show keyboard shortcuts
            e.preventDefault();
            setIsKeyboardShortcutsOpen(true);
            break;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate]);

  // Update mobile view state when screen size changes
  useEffect(() => {
    dispatch(setIsMobileView(isMobile));
  }, [dispatch, isMobile]);

  return (
    <Root>
      <CssBaseline />
      
      {/* Skip to main content link for screen reader users */}
      <SkipLink href="#main-content" tabIndex={0}>
        Skip to main content
      </SkipLink>
      
      {/* TopBar - Fixed position at the top */}
      <TopBarContainer>
        <TopBar />
      </TopBarContainer>
      
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main content — Drawer already reserves width in the flex row.
          Do NOT also apply marginLeft or the gap doubles (blank column). */}
      <Main
        sx={{
          width: 'auto',
          flexGrow: 1,
          ml: 0,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <ContentContainer maxWidth={false}>
          <Outlet />
        </ContentContainer>
      </Main>
      
      {/* Accessibility help button with enhanced screen reader support */}
      <KeyboardHelpFab
        $raised={isSelectionMode}
        aria-label="Keyboard shortcuts help"
        title="Show keyboard shortcuts (F1)"
        color="primary" 
        size="medium"
        onClick={() => setIsKeyboardShortcutsOpen(true)}
      >
        <KeyboardIcon />
      </KeyboardHelpFab>
      
      {/* Keyboard shortcuts dialog */}
      <KeyboardShortcutsHelp 
        open={isKeyboardShortcutsOpen} 
        onClose={() => setIsKeyboardShortcutsOpen(false)} 
      />
      
      {/* Dialog component */}
      <ConfirmDialog />
      
      {/* Background upload notification */}
      <BackgroundUploadNotification 
        onOpenUploadDialog={requestLayoutUpload} 
      />
      
      {/* Single app-level upload dialog */}
      <SimpleFolderUploadDialog
        open={uploadDialogOpen && Boolean(layoutUploadFolderId)}
        onClose={() => dispatch(closeUploadDialog())}
        folderId={layoutUploadFolderId ?? undefined}
        onUploadComplete={() => {
          dispatch(closeUploadDialog());
        }}
      />
    </Root>
  );
};

export default MainLayout; 